import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Badge, Button } from "@realtyflows/ui";
import { getEsignQuota } from "../api";

/**
 * EsignQuotaMeter — V1 e-sign envelope usage meter.
 *
 * V1 Section B reframe: agents think in "envelopes" (one folder send =
 * one envelope, regardless of how many docs are inside), so we label
 * everything in envelope terms — even though the underlying counter
 * (signings_used) is unchanged.
 *
 * Surfaces:
 *  • Current month's envelope usage (used / limit / % bar)
 *  • Add-on pack balance (purchased extras)
 *  • Trial envelopes remaining (only for non-active accounts)
 *  • Days until quota reset
 *  • Color-coded warning levels: ok (emerald) → warning (amber, ≥80%) → exhausted (coral, 100%)
 *  • Buy-more / Upgrade CTAs at appropriate thresholds
 *
 * Used on the Billing page; re-usable on the Dashboard or Folders page.
 */
export default function EsignQuotaMeter({ compact = false }) {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEsignQuota()
      .then(setQuota)
      .catch(() => setQuota(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !quota) return null;

  // Color scheme reflects warning level
  const color =
    quota.warningLevel === "exhausted" ? t.coral
    : quota.warningLevel === "warning" ? t.amber
    : t.emerald;

  const planLabel = (quota.plan || "free").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Panel padding={compact ? 14 : 20} style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            E-Sign Envelopes
          </span>
          <Badge color={color}>{planLabel}</Badge>
        </div>
        <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          Resets in {quota.daysUntilReset} {quota.daysUntilReset === 1 ? "day" : "days"}
        </span>
      </div>

      {/* Used / Limit numbers */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono, lineHeight: 1 }}>
          {quota.used}
        </span>
        <span style={{ fontSize: 14, color: t.textMuted, fontFamily: font.mono }}>
          / {quota.limit > 0 ? quota.limit : "—"} envelopes this month
        </span>
      </div>
      <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 8, fontFamily: font.body }}>
        One envelope = one folder send (any number of documents inside).
      </div>

      {/* Progress bar */}
      <div style={{ height: 8, background: t.border, borderRadius: 99, overflow: "hidden", marginBottom: 12 }}>
        <div
          style={{
            width: `${Math.min(100, quota.percentUsed)}%`,
            height: "100%",
            background: color,
            transition: "width 0.3s ease",
          }}
        />
      </div>

      {/* Sub-stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: quota.warningLevel !== "ok" ? 14 : 0 }}>
        <Stat label="Remaining" value={quota.remaining} color={t.emerald} />
        <Stat label="Add-on Balance" value={quota.addonBalance} color={t.sky} />
        <Stat label="Free Trial" value={quota.freeRemaining} color={t.amber} />
      </div>

      {/* Warning banner */}
      {quota.warningLevel === "warning" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: t.amber + "15", border: `1px solid ${t.amber}30`, borderRadius: 8 }}>
          <div style={{ flex: 1, fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>
            You've used <strong>{quota.percentUsed}%</strong> of your monthly envelopes. Buy an add-on pack or upgrade to avoid interruption.
          </div>
          <Button variant="secondary" onClick={() => navigate("/billing?tab=signings")}>Buy more</Button>
        </div>
      )}

      {quota.warningLevel === "exhausted" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: t.coral + "15", border: `1px solid ${t.coral}30`, borderRadius: 8 }}>
          <div style={{ flex: 1, fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>
            <strong>You're out of envelopes this month.</strong> Buy an add-on pack or upgrade your plan to continue sending folders for signing.
          </div>
          <Button variant="primary" onClick={() => navigate("/billing?tab=signings")}>Buy now</Button>
        </div>
      )}
    </Panel>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: t.surfaceWarm, borderRadius: 6, padding: "8px 10px" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color, fontFamily: font.mono }}>{value}</div>
      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.body, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
    </div>
  );
}
